import { breakpoints, type Breakpoint } from "@/lib/design-tokens/breakpoints";
import { useEffect, useState, type ReactNode } from "react";

/**
 * Hook to check if the current viewport matches a given breakpoint
 * @param breakpoint - The breakpoint name to check (e.g., 'md', 'lg')
 * @returns boolean - true if the viewport is at or above the breakpoint
 */
export default function useMediaQuery(breakpoint: Breakpoint): boolean {
  // Initialize with the current match state
  const [matches, setMatches] = useState(() => {
    return window.matchMedia(`(min-width: ${breakpoints[breakpoint]})`).matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(
      `(min-width: ${breakpoints[breakpoint]})`,
    );

    // Create event listener that updates state
    const handler = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Check if state needs updating (e.g., breakpoint prop changed)
    // Use requestAnimationFrame to defer state update outside effect body
    const rafId = requestAnimationFrame(() => {
      if (mediaQuery.matches !== matches) {
        setMatches(mediaQuery.matches);
      }
    });

    // Add listener
    mediaQuery.addEventListener("change", handler);

    // Cleanup
    return () => {
      cancelAnimationFrame(rafId);
      mediaQuery.removeEventListener("change", handler);
    };
  }, [breakpoint, matches]);

  return matches;
}

/**
 * Wrapper component to conditionally render content based on a media query.
 * Useful if you don't want to use the hook directly in the component since it'll trigger a re-render.
 * @param breakpoint - The breakpoint name to check (e.g., 'md', 'lg')
 * @param children - The content to render if the media query matches
 * @returns The rendered content
 */
export function WithMediaQuery({
  children,
  breakpoint,
}: {
  children: ({ matches }: { matches: boolean }) => ReactNode;
  breakpoint: Breakpoint;
}) {
  const matches = useMediaQuery(breakpoint);
  return children({ matches });
}
